/**
 * String Formatting Utilities
 * This module provides utilities for formatting strings.
 */
import { IUserSession } from 'types'
import { IPatientResponse } from 'types/patient/types'

/**
 * @description Formats a string to a full name by combining first and last names.
 * @param data - The data object containing first and last names
 * @returns {string} - The formatted full name
 */
export const getFullName = (data: IPatientResponse | IUserSession): string =>
  `${data.first_name.trim()} ${data.last_name.trim()}`

/**
 * @description Removes accents from a string.
 * @param text - The text to remove accents from
 * @returns {string} - The text without accents
 */
export const removeAccents = (text: string): string =>
  text.normalize('NFD').replaceAll(/[\u0300-\u036f]/g, '')

/**
 * @description Trims all string values in a JSON object.
 * @param data - The JSON object to trim
 * @returns {T} - A new object with all string values trimmed
 */
export const trimStringInJSON = <T extends object = any>(data: T): T => {
  if (typeof data == 'object') {
    const newData: T = { ...data }

    for (const index in data) {
      const key = index as keyof T
      const value = data[key]
      newData[key] = (typeof value == 'string' ? value.trim() : value) as any
    }

    return newData
  }
  return data
}
